<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: MediaOverlayHeader                                    |
| Section: components                                              |
| Purpose: Shared accessible header and metadata for media viewers.|
+------------------------------------------------------------------+
-->
<template>
    <header class="media-overlay-header" @click.stop @mousedown.stop>
        <div class="media-overlay-header__identity">
            <p class="media-overlay-header__context">
                <span>{{ mediaTypeLabel }}</span>
                <template v-if="showPosition">
                    <span aria-hidden="true">·</span>
                    <span>{{ positionLabel }}</span>
                </template>
            </p>
            <h2 :id="headingId" class="media-overlay-header__title">{{ displayTitle }}</h2>
        </div>

        <div class="media-overlay-header__actions">
            <button v-if="hasDetails" ref="detailsButton" type="button" class="media-overlay-header__button media-overlay-header__details-button"
                :aria-expanded="detailsOpen" :aria-controls="detailsId" @click="toggleDetails">
                <img src="@/assets/icons/info.svg" alt="" aria-hidden="true">
                <span>{{ $t('mediaOverlay.details') }}</span>
            </button>
            <button ref="closeButton" type="button" class="media-overlay-header__button media-overlay-header__close-button"
                :aria-label="$t('mediaOverlay.close')" @click="$emit('close')">
                <img src="@/assets/icons/close.svg" alt="" aria-hidden="true">
            </button>
        </div>

        <section v-if="detailsOpen" :id="detailsId" ref="detailsPanel" class="media-overlay-header__panel" role="region"
            :aria-label="$t('mediaOverlay.details')">
            <p class="media-overlay-header__full-title">{{ displayTitle }}</p>

            <dl class="media-overlay-header__metadata">
                <div v-if="owner" class="media-overlay-header__metadata-row">
                    <dt>{{ $t('mediaOverlay.source') }}</dt>
                    <dd>{{ owner }}</dd>
                </div>
                <div v-if="normalizedLinkedPersonIDs.length" class="media-overlay-header__metadata-row">
                    <dt>{{ $t('mediaOverlay.linkedTo') }}</dt>
                    <dd>
                        <span v-if="detailsLoading" role="status">{{ $t('mediaOverlay.loadingDetails') }}</span>
                        <ul v-else-if="linkedPersons.length">
                            <li v-for="person in linkedPersons" :key="person.id">
                                <router-link :to="localePath(`/person/${person.id}`)">{{ person.name }}</router-link>
                            </li>
                        </ul>
                        <span v-else>{{ $t('mediaOverlay.detailsUnavailable') }}</span>
                        <span v-if="!detailsLoading && detailsError && linkedPersons.length" class="media-overlay-header__warning">
                            {{ $t('mediaOverlay.someDetailsUnavailable') }}
                        </span>
                    </dd>
                </div>
            </dl>
        </section>
    </header>
</template>

<script>
import { getPersonById } from '@/api/personService';
import { useLocale } from '@/composables/useLocale';
import { normalizeLinkedPersonIDs } from '@/utils/mediaUtils';

const personCache = new Map();

function getCachedPerson(id) {
    if (personCache.has(id)) return personCache.get(id);

    const request = getPersonById(id)
        .then((person) => {
            if (!person) return null;
            return {
                id: person.personID || id,
                name: `${person.firstname || ''} ${person.lastname || ''}`.trim() || id
            };
        })
        .catch((error) => {
            personCache.delete(id);
            throw error;
        });

    personCache.set(id, request);
    return request;
}

export default {
    name: 'MediaOverlayHeader',
    emits: ['close'],
    props: {
        title: { type: String, default: '' },
        mediaType: { type: String, default: 'image' },
        mediaKind: { type: String, default: 'unknown' },
        owner: { type: String, default: '' },
        linkedPersonIds: { type: Array, default: () => [] },
        currentIndex: { type: Number, default: -1 },
        totalItems: { type: Number, default: 0 },
        headingId: { type: String, default: 'media-overlay-title' },
        detailsId: { type: String, default: 'media-overlay-details' }
    },
    setup() {
        const { localePath } = useLocale();
        return { localePath };
    },
    data() {
        return {
            detailsOpen: false,
            detailsLoading: false,
            detailsError: false,
            linkedPersons: [],
            detailsRequestId: 0
        };
    },
    computed: {
        fullTitle() {
            return String(this.title || '').trim() || this.$t('mediaOverlay.untitled');
        },
        displayTitle() {
            if (this.mediaKind !== 'document') return this.fullTitle;

            const separatorIndex = this.fullTitle.indexOf(' – ');
            if (separatorIndex < 1) return this.fullTitle;

            const subject = this.fullTitle.slice(0, separatorIndex).trim();
            const source = this.fullTitle.slice(separatorIndex + 3).trim();
            return subject && source ? subject : this.fullTitle;
        },
        mediaTypeLabel() {
            const isPhoto = this.mediaKind === 'photo'
                || (this.mediaKind === 'unknown' && this.mediaType === 'image');
            return this.$t(isPhoto ? 'mediaOverlay.photo' : 'mediaOverlay.document');
        },
        normalizedLinkedPersonIDs() {
            return normalizeLinkedPersonIDs(this.linkedPersonIds);
        },
        hasDetails() {
            return Boolean(String(this.owner || '').trim() || this.normalizedLinkedPersonIDs.length);
        },
        showPosition() {
            return this.totalItems > 1 && this.currentIndex >= 0 && this.currentIndex < this.totalItems;
        },
        positionLabel() {
            return this.$t('mediaOverlay.position', {
                current: this.currentIndex + 1,
                total: this.totalItems
            });
        }
    },
    watch: {
        title: 'resetDetails',
        mediaKind: 'resetDetails',
        owner: 'resetDetails',
        linkedPersonIds: {
            deep: true,
            handler: 'resetDetails'
        }
    },
    methods: {
        focusCloseButton() {
            this.$refs.closeButton?.focus();
        },
        closeDetails({ restoreFocus = true } = {}) {
            if (!this.detailsOpen) return false;
            this.detailsOpen = false;
            if (restoreFocus) {
                this.$nextTick(() => this.$refs.detailsButton?.focus());
            }
            return true;
        },
        toggleDetails() {
            if (this.detailsOpen) {
                this.closeDetails();
                return;
            }

            this.detailsOpen = true;
            if (this.normalizedLinkedPersonIDs.length && !this.linkedPersons.length) {
                this.loadLinkedPersons();
            }
        },
        resetDetails() {
            this.detailsRequestId += 1;
            this.detailsOpen = false;
            this.detailsLoading = false;
            this.detailsError = false;
            this.linkedPersons = [];
        },
        async loadLinkedPersons() {
            const requestId = ++this.detailsRequestId;
            this.detailsLoading = true;
            this.detailsError = false;

            const results = await Promise.allSettled(
                this.normalizedLinkedPersonIDs.map((id) => getCachedPerson(id))
            );

            if (requestId !== this.detailsRequestId) return;

            this.linkedPersons = results
                .filter((result) => result.status === 'fulfilled' && result.value)
                .map((result) => result.value);
            this.detailsError = results.some((result) => result.status === 'rejected');
            this.detailsLoading = false;
        }
    }
};
</script>

<style scoped>
.media-overlay-header {
    position: relative;
    z-index: 20;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 20px;
    width: 100%;
    min-height: 80px;
    padding: 12px 16px 12px 20px;
    color: var(--brand-text-light);
    background: var(--brand-bg-4);
    border-bottom: 2px solid rgba(255, 255, 255, 0.24);
}

.media-overlay-header__identity {
    min-width: 0;
}

.media-overlay-header__context {
    display: flex;
    gap: 6px;
    margin: 0 0 2px;
    color: rgba(255, 255, 255, 0.68);
    font-size: var(--font-size-xs);
    font-weight: 600;
    line-height: 1.4;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.media-overlay-header__title {
    display: -webkit-box;
    overflow: hidden;
    margin: 0;
    color: var(--brand-text-light);
    font-family: var(--font-primary);
    font-size: var(--font-size-lg);
    font-weight: 600;
    line-height: 1.35;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
}

.media-overlay-header__actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.media-overlay-header__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    min-height: 40px;
    margin: 0;
    padding: 0;
    color: var(--brand-text-light);
    background: transparent;
    border: 0;
    border-radius: var(--border-radius-pill);
    cursor: pointer;
    transition: var(--transition-bounce);
}

.media-overlay-header__button:hover,
.media-overlay-header__button:focus-visible {
    box-shadow: 0 0 0 4px var(--brand-color-1);
    outline: none;
}

.media-overlay-header__details-button {
    gap: 8px;
    padding: 0 12px;
    border: 1px solid rgba(255, 255, 255, 0.32);
    border-radius: var(--border-radius-md);
    font: inherit;
    font-size: var(--font-size-sm);
    font-weight: 600;
}

.media-overlay-header__details-button[aria-expanded="true"] {
    background: rgba(255, 255, 255, 0.12);
}

.media-overlay-header__details-button img {
    width: 16px;
    height: 16px;
}

.media-overlay-header__close-button img {
    width: 28px;
    height: 28px;
}

.media-overlay-header__panel {
    position: absolute;
    top: calc(100% + 10px);
    right: 16px;
    width: min(420px, calc(100vw - 32px));
    max-height: min(480px, calc(100vh - 128px));
    overflow: auto;
    padding: 18px;
    color: var(--brand-text-dark);
    background: var(--brand-bg-1);
    border-radius: var(--border-radius-md);
    box-shadow: var(--shadow-card-lg);
}

.media-overlay-header__full-title {
    margin: 0 0 16px;
    font-weight: 700;
    line-height: 1.4;
    overflow-wrap: anywhere;
}

.media-overlay-header__metadata,
.media-overlay-header__metadata-row,
.media-overlay-header__metadata dd {
    margin: 0;
}

.media-overlay-header__metadata-row + .media-overlay-header__metadata-row {
    margin-top: 14px;
}

.media-overlay-header__metadata dt {
    margin-bottom: 2px;
    color: rgba(29, 29, 29, 0.64);
    font-size: var(--font-size-xs);
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
}

.media-overlay-header__metadata ul {
    margin: 0;
    padding-left: 0;
}

.media-overlay-header__metadata a {
    color: var(--brand-color-1);
    font-weight: 600;
}

.media-overlay-header__warning {
    display: block;
    margin-top: 8px;
    color: rgba(29, 29, 29, 0.68);
    font-size: var(--font-size-xs);
}

@media (max-width: 860px) {
    .media-overlay-header {
        min-height: 72px;
        gap: 8px;
        padding: 10px 12px;
    }

    .media-overlay-header__title {
        font-size: var(--font-size-base);
    }

    .media-overlay-header__details-button {
        width: 40px;
        padding: 0;
        border-radius: var(--border-radius-pill);
    }

    .media-overlay-header__details-button span {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
    }

    .media-overlay-header__panel {
        top: 100%;
        right: 0;
        left: 0;
        width: 100%;
        max-height: min(50vh, 420px);
        border-radius: 0 0 var(--border-radius-md) var(--border-radius-md);
    }
}
</style>
